import type { ModelConfigEntry } from "@/client/config/models.config";
import type { SchemaGenerationPayload } from "@/client/types/generation";
import {
  isCustomFormConfig,
  isSchemaFormConfig,
  type ModelFormSubmitContext,
} from "@/features/workspace/modelFormTypes";
import type { SchemaFormDefinition, SchemaFormValues } from "@/features/workspace/schema/types";
import { deriveInitialValues } from "@/features/workspace/schema/helpers";
import { buildSchemaPayload } from "@/features/workspace/schema/buildPayload";

type SchemaModule =
  | SchemaFormDefinition<string>
  | { default: SchemaFormDefinition<string> }
  | { schema: SchemaFormDefinition<string> };

const schemaCache = new Map<string, Promise<SchemaFormDefinition<string>>>();

const resolveSchemaDefinition = (mod: SchemaModule): SchemaFormDefinition<string> => {
  if (!mod) {
    throw new Error("schema 模块未返回有效定义");
  }
  if (typeof mod === "object") {
    if ("schema" in mod && mod.schema) {
      return mod.schema as SchemaFormDefinition<string>;
    }
    if ("default" in mod && mod.default) {
      return mod.default as SchemaFormDefinition<string>;
    }
  }
  return mod as SchemaFormDefinition<string>;
};

const normalizeSchemaLoadError = (modelKey: string, reason: unknown): Error => {
  const detail =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : String(reason);
  return new Error(`模型 ${modelKey} schema 加载失败：${detail || "未知错误"}`);
};

export const loadWorkspaceSchema = async (
  config: ModelConfigEntry
): Promise<SchemaFormDefinition<string>> => {
  const { modelKey } = config;

  const cached = schemaCache.get(modelKey);
  if (cached) {
    return cached;
  }

  const formConfig = config.workspace?.form;
  if (!isSchemaFormConfig(formConfig)) {
    throw new Error(`模型 ${modelKey} 未配置 Schema 表单`);
  }

  const loadPromise = Promise.resolve()
    .then(() => formConfig.load())
    .then((mod) => resolveSchemaDefinition(mod))
    .catch((reason) => {
      throw normalizeSchemaLoadError(modelKey, reason);
    });

  const managedPromise = loadPromise.catch((error) => {
    schemaCache.delete(modelKey);
    throw error;
  });

  schemaCache.set(modelKey, managedPromise);
  return managedPromise;
};

export const normalizeSchemaValues = (
  schema: SchemaFormDefinition<string>,
  rawValues: unknown
): SchemaFormValues<string> => {
  const defaults = deriveInitialValues(schema);
  if (!rawValues || typeof rawValues !== "object" || rawValues instanceof FormData) {
    return defaults;
  }

  const normalized: SchemaFormValues<string> = { ...defaults };
  const entries = Object.entries(rawValues as Record<string, unknown>);
  entries.forEach(([key, value]) => {
    normalized[key] = value;
  });
  return normalized;
};

export const buildPayloadFromConfig = async (
  config: ModelConfigEntry,
  values: unknown
): Promise<SchemaGenerationPayload> => {
  const formConfig = config.workspace?.form;
  if (!formConfig) {
    throw new Error(`模型 ${config.modelKey} 未配置任何表单`);
  }

  if (isCustomFormConfig(formConfig)) {
    const context: ModelFormSubmitContext<unknown> = {
      modelKey: config.modelKey,
      values,
    };
    return formConfig.buildPayload(values, context);
  }

  if (isSchemaFormConfig(formConfig)) {
    const schema = await loadWorkspaceSchema(config);
    const normalizedValues = normalizeSchemaValues(schema, values);
    return buildSchemaPayload({
      schema,
      values: normalizedValues,
      modelKey: config.modelKey,
    });
  }

  throw new Error(`模型 ${config.modelKey} 使用了未知的表单配置类型`);
};
